import { ObjectPKInput, ObjectSKInput } from "../aws/DynamoDbApiTypes";
import { DynamoKey } from "./BaseDynamoClass";
import { StripUnderscore, UnderscoreKeys } from "../util/Types";
import { BasicError } from "./BasicError";
import { OrderStatus } from "./Enums";
import { User } from "./User";
import { UserOwnedClass, userOwnedSchema } from "./UserOwnedClass";
import z from "zod";

export interface OrderPKInput extends ObjectPKInput {
	orderId: string;
}

export const orderBaseSchema = userOwnedSchema.extend({
  _orderId: z.string().nonempty(),
  _orderShortRef: z.string().nonempty(),
  _currency: z.string().nonempty(),
  _totalAmount: z.number(),
  _totalTax: z.number(),
});

export type OrderBaseFields = StripUnderscore<z.infer<typeof orderBaseSchema>>;

export class OrderBase<TFields extends OrderBaseFields = OrderBaseFields> extends UserOwnedClass<TFields> {
	_orderId!: string;
	_orderShortRef!: string;
	_currency!: string;
	_totalAmount!: number;
	_totalTax!: number;
	override _className: string = "Order";
	override _deserializeClassName: string = "OrderBase";

	override getFieldList(): (keyof UnderscoreKeys<TFields>)[] {
		return [...super.getFieldList(), "_orderId", "_orderShortRef", "_currency", "_totalAmount", "_totalTax"];
	}
	constructor(props: Partial<TFields>) {
		super(props);
	}
	override validateSchema(): void {
		orderBaseSchema.parse(this);
	}
	get orderId(): string {
		return this.checkFieldType<"orderId">(this._orderId);
	}
	set orderId(value: string) {
		this._orderId = this.checkFieldTypeAndMark<"orderId">("_orderId", value);
	}

	get orderShortRef(): string {
		return this.checkFieldType<"orderShortRef">(this._orderShortRef);
	}
	set orderShortRef(value: string) {
		this._orderShortRef = this.checkFieldTypeAndMark<"orderShortRef">("_orderShortRef", value);
	}

	get currency(): string {
		return this.checkFieldType<"currency">(this._currency);
	}
	set currency(value: string) {
		this._currency = this.checkFieldTypeAndMark<"currency">("_currency", value);
	}

	get totalAmount(): number {
		return this.checkFieldType<"totalAmount">(this._totalAmount);
	}
	set totalAmount(value: number) {
		this._totalAmount = this.checkFieldTypeAndMark<"totalAmount">("_totalAmount", value);
	}

	get totalTax(): number {
		return this.checkFieldType<"totalTax">(this._totalTax);
	}
	set totalTax(value: number) {
		this._totalTax = this.checkFieldTypeAndMark<"totalTax">("_totalTax", value);
	}
	getPKPrefix(): string {
		return "ORDER#";
	}
	getSKPrefix(props: object | undefined): string {
		return "ORDER#";
	}
	static getPKPrefix(): string {
		return "ORDER#";
	}
	static getSKPrefix(props: object | undefined): string {
		return "ORDER#";
	}
	getPK(): string {
		return this.getPKPrefix() + this.orderId;
	}
	getSK(): string {
		return this.getSKPrefix(this) + this.orderId;
	}
	getParentKey(): DynamoKey | undefined {
		if (!this.orderId) throw new BasicError("Missing userId", "Missing userId");
		return User.generateKey({ userId: this.userId });
	}
	getParentKeyInput(): ObjectPKInput | undefined {
		if (!this.userId) throw new BasicError("Missing userId", "Missing userId");
		return { userId: this.userId };
	}
	getParentTableName(): string | undefined {
		return undefined;
	}
	generateKey(input: any): DynamoKey {
		if (!input.orderId) throw new BasicError("Missing orderId", "Missing orderId");
		return {
			PK: this.getPKPrefix + input.orderId,
			SK: this.getSKPrefix(input) + input.orderId,
		};
	}
	static generateKey(input: any): DynamoKey {
		if (!input.orderId) throw new BasicError("Missing orderId", "Missing orderId");
		return {
			PK: OrderBase.getPKPrefix() + input.orderId,
			SK: OrderBase.getSKPrefix(input) + input.orderId,
		};
	}
	generatePK(input: OrderPKInput): string {
		if (!input.orderId) throw new BasicError("Missing orderId", "Missing orderId");
		return this.getPKPrefix() + input.orderId;
	}
	generateGSIPK(index: number, key: ObjectPKInput): string {
		switch (index) {
			case 1:
			case 2:
			default:
				return "USER#" + (!!key && key.userId ? key.userId : "");
		}
	}
	generateGSISK(index: number, key: ObjectSKInput): string {
		switch (index) {
			case 2:
				return `SESSION#${key._paymentSessionId}#ORDER#${key.orderId ? key.orderId : ""}`;
			case 1:
			default:
				return this.getSKPrefix(key) + (!!key && key.orderId ? key.orderId : "");
		}
	}
	getGlobalIndices(): object {
		return {
			GSI1PK: this.generateGSIPK(1, this),
			GSI1SK: this.generateGSISK(1, this),
			GSI2PK: this.generateGSIPK(2, this),
			GSI2SK: this.generateGSISK(2, this),
		};
	}
	getColumnsForExport(): string[] {
		throw new Error("Method not implemented.");
	}
	getObjectForExport(): object {
		throw new Error("Method not implemented.");
	}
	getSimplifiedStatus(): string {
		return OrderBase.getSimplifiedStatus(this.status);
	}

	static getSimplifiedStatus(status : number | undefined){
		switch (status) {
			case OrderStatus.CANCELLED:
				return "Order cancelled";
			case OrderStatus.CREATED:
				return "Order created";
			case OrderStatus.PENDING_PAYMENT:
				return "Order pending payment";
			case OrderStatus.PAYMENT_FAILED:
				return "Order payment failed";
			case OrderStatus.PAID:
				return "Order paid";
			case OrderStatus.PRODUCTION_DELAYED:
			case OrderStatus.AWAITING_PRODUCTION:
				return "Awaiting production";
			case OrderStatus.IN_PRODUCTION:
				return "In production";
			case OrderStatus.PRODUCTION_FAILED:
				return "Production issue";
			case OrderStatus.PRODUCTION_COMPLETE:
			case OrderStatus.AWAITING_FULFILLMENT:
			case OrderStatus.FULFILLED:
				return "Awaiting shipment";
			case OrderStatus.SHIPPED:
			case OrderStatus.OUT_FOR_DELIVERY:
				return "Shipped";
			case OrderStatus.DELIVERED:
				return "Delivered";
			case OrderStatus.DELIVERY_FAILED:
				return "Delivery issue";
			case OrderStatus.RETURNED:
			case OrderStatus.REFUNDED:
			case OrderStatus.IN_DISPUTE:
			case OrderStatus.REPLACEMENT_SENT:
				return "In returns process";
			default:
				return "Unknown";
		}
	}
}
